<template>
	<div class="transcription-summary">
		<header class="header">
			<div>
				<FontAwesomeIcon
					:icon="[ `fas`, `message-bot` ]"
				/>
			</div>
			<div>
				<div class="header--left">
					<h1 class="title">{{ translate( `title` ) }}</h1>
				</div>
				<p class="description">{{ translate( `description` ) }}</p>
				<!-- FEATUREFLAG -->
				<div class="header--right" v-if="$config().enableTranscriptionRating">
					<label class="rating-label">{{ translate( `isThisHelpfulLabel` ) }}</label>
					<ThumbsUpButton
						size="TINY"
						:type="thumbsUpButtonType"
						:borderless="true"
						@click="rateTranscription( 1 )"
					/>
					<ThumbsDownButton
						size="TINY"
						:type="thumbsDownButtonType"
						:borderless="true"
						@click="rateTranscription( 0 )"
					/>
				</div>
			</div>
		</header>
		<div class="header-line"></div>
		<ShowMore
			@expand="$emit( `expandDetails`, { subDocId } )"
			@collapse="$emit( `collapseDetails`, { subDocId } )"
		>
			<template #primary>
				<main class="body" v-html="formattedSummary"></main>
			</template>
			<template #secondary v-if="itemizedSummary.length">
				<div class="transcription-summary__itemized-summaries">
					<TranscriptionItemizedSummary v-for="summary in itemizedSummary" :key="summary.title"
						:title="summary.title"
						:key-details="summary.keyDetails"
						:action-items="summary.actionItems"
						@recommendActionItem="$emit( `recommendActionItem`, { ...$event, subDocId } )"
					/>
				</div>
			</template>
			<template #more="{ toggle }">
				<div class="transcription-summary__show-more-button more">
					<TeamingButton2
						size="TINY"
						type="TERTIARY"
						@click.stop.prevent="toggle"
					>
						<template #left-icon>
							<FontAwesomeIcon :icon="[ `far`, `chevron-double-down` ]" />
						</template>
						<template #default>{{ translate( `expandDetailsLabel` ) }}</template>
					</TeamingButton2>
					<TeamingButton2
						type="TERTIARY"
						size="TINY"
						@click.stop.prevent="$emit( `viewTranscription`, { subDocId } )"
					>
						{{ translate( `viewTranscriptButton` ) }}
					</TeamingButton2>
				</div>
			</template>
			<template #fewer="{ toggle }">
				<div class="transcription-summary__show-more-button less">
					<TeamingButton2
						size="TINY"
						type="TERTIARY"
						@click.stop.prevent="toggle"
					>
						<template #left-icon>
							<FontAwesomeIcon :icon="[ `far`, `chevron-double-up` ]" />
						</template>
						<template #default>{{ translate( `collapseLabel` ) }}</template>
					</TeamingButton2>
					<TeamingButton2
						type="TERTIARY"
						size="TINY"
						@click.stop.prevent="$emit( `viewTranscription`, { subDocId } )"
					>
						{{ translate( `viewTranscriptButton` ) }}
					</TeamingButton2>
				</div>
			</template>
		</ShowMore>
		<footer class="footer" v-if="!itemizedSummary.length">
			<TeamingButton2
				type="TERTIARY"
				size="TINY"
				@click="$emit( `viewTranscription`, { subDocId } )"
			>
				{{ translate( `viewTranscriptButton` ) }}
			</TeamingButton2>
		</footer>
	</div>
</template>

<script>
import DOMPurify from "dompurify";
import { markdown } from "@teaming/common-util";

import {
	EventsMixin,
	ShowMore,
	TeamingButton2,
	ThumbsUpButton,
	ThumbsDownButton
} from "@teaming/common-ui";

import TranscriptionItemizedSummary from "./TranscriptionItemizedSummary.vue";

export default {
	name: "TranscriptionSummary",
	mixins: [ EventsMixin, ],
	emits: [
		"collapseDetails",
		"expandDetails",
		"rateTranscription",
		"recommendActionItem",
		"viewTranscription",
	],
	components: {
		ShowMore,
		TeamingButton2,
		ThumbsUpButton,
		ThumbsDownButton,
		TranscriptionItemizedSummary,
	},
	props: {
		subDocId: {
			type: String,
			required: true,
		},
		summary: {
			type: String,
			required: true,
		},
		itemizedSummary: {
			type: Array,
			required: true,
		},
		rating: {
			type: Number,
			required: true,
		},
	},
	computed: {
		formattedSummary() {
			const { summary, } = this;
			const renderedSummary = markdown.parse( summary || "" );
			return DOMPurify.sanitize( renderedSummary );
		},
		thumbsUpButtonType() {
			const { rating, } = this;
			return rating === 1 ? "PRIMARY" : "SECONDARY";
		},
		thumbsDownButtonType() {
			const { rating, } = this;
			return rating === 0 ? "PRIMARY" : "SECONDARY";
		},
	},
	methods: {
		translate( key, args = {} ) {
			const { name, } = this.$options;
			return this.$t( `common-team.components.${ name }.${ key }`, args );
		},
		rateTranscription( newRating ) {
			const { subDocId, rating, } = this;
			if ( newRating === rating ) {
				return;
			}
			this.$emit( "rateTranscription", { subDocId, rating: newRating, } );
		},
	},
};
</script>

<style lang="scss" scoped>
$titleColor: $color9-600;
$textColor: $color9-500;
$backgroundColor: $color9-050;
$ratingLabelColor: $color2-500;

.transcription-summary {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	gap: 1rem;

	padding: 1rem;

	background-color: $backgroundColor;
	border-radius: 0.5rem;
}

.transcription-summary .header {
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: flex-start;
	gap: 0.5rem;

	padding: 1rem 1rem 0 1rem;

	svg {
		color: $textColor;
	}
}

.transcription-summary .header-line {
	height: 0.0625rem;
	background-color: $color1-200;
	border: none;
	margin: 0.5rem 0;
}

.transcription-summary .header--left,
.transcription-summary .header--right {
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: center;
	gap: 0.5rem;
}

.transcription-summary .header--right {
	justify-content: flex-end;
	gap: 0.3rem;
}

.transcription-summary .title {
	color: $titleColor;
	@include font-and-line-for(0);
	font-weight: 600;
}

.transcription-summary .description {
	color: $textColor;
	@include font-and-line-for(0);
}

.transcription-summary .rating-label {
	color: $ratingLabelColor;
	@include font-and-line-for(-1);
	font-weight: 450;
}

.transcription-summary .body {
	@include font-and-line-for(0);
	padding: 0 1rem 1rem 1rem;
}

.transcription-summary .footer {
	display: flex;
	justify-content: flex-end;
}

.transcription-summary__show-more-button {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
}

.transcription-summary__itemized-summaries {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	gap: 1rem;

	padding: 1rem;

	border-top: 0.0625rem solid $borderColor--dark;
}
</style>

<style lang="scss">
$bodyColor: $color1-700;

.transcription-summary .body {
	color: $bodyColor;

	ul, ol {
		padding-left: 0.5rem;
	}
}
</style>
